import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import dryftService from '../services/dryftService';
import './AddPaymentPorterWizard.css';
import './OperaCloudCompare.css';

// Create/edit a Dryft Notification Target — a named recipient group of up to 3 emails + one Teams
// channel. A project route alerts a target (by id) when its watched config set changes.

const BACK = '/apps/dryft?tab=notification-targets';
const MAX_EMAILS = 3;

const DryftNotificationTargetForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;
    const { selectedAccount, selectedPartner } = useAccount();

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [emails, setEmails] = useState(['']);
    const [teamsWebhook, setTeamsWebhook] = useState('');
    const [active, setActive] = useState(true);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [nameErr, setNameErr] = useState(null);

    const load = useCallback(async () => {
        if (!isEdit) return;
        setLoading(true);
        setError(null);
        try {
            const res = await dryftService.getNotificationTarget(id);
            const t = res.data?.target;
            if (t) {
                setName(t.name || '');
                setDescription(t.description || '');
                setActive(t.active !== 0 && t.active !== false);
                setTeamsWebhook(t.teams_webhook || '');
                setEmails(t.emails && t.emails.length ? t.emails.slice(0, MAX_EMAILS) : ['']);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load notification target');
        } finally {
            setLoading(false);
        }
    }, [id, isEdit]);

    useEffect(() => { load(); }, [load]);

    const setEmailAt = (i, val) => setEmails(prev => prev.map((e, idx) => (idx === i ? val : e)));
    const addEmail = () => setEmails(prev => (prev.length < MAX_EMAILS ? [...prev, ''] : prev));
    const removeEmail = (i) => setEmails(prev => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : ['']));

    const handleSave = async () => {
        if (!name.trim()) { setNameErr('Name is required'); return; }
        const cleanEmails = emails.map(e => e.trim()).filter(Boolean);
        if (cleanEmails.length === 0 && !teamsWebhook.trim()) {
            setError('Provide at least one email or a Teams channel');
            return;
        }
        setSaving(true);
        setError(null);
        const payload = {
            name: name.trim(),
            description: description.trim() || null,
            active,
            teams_webhook: teamsWebhook.trim() || null,
            emails: cleanEmails,
            account_id: selectedAccount || null,
            partner_id: selectedPartner || null,
        };
        try {
            if (isEdit) await dryftService.updateNotificationTarget(id, payload);
            else await dryftService.createNotificationTarget(payload);
            navigate(BACK);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save notification target');
            setSaving(false);
        }
    };

    return (
        <div className="ppw-page">
            <div className="ppw-card">
                <div className="ppw-card-header">
                    <h2 className="ppw-card-title">{isEdit ? 'Edit notification target' : 'New notification target'}</h2>
                    <p className="ppw-card-subtitle">A named recipient group — up to 3 emails and one Teams channel — that a project alerts on config changes.</p>
                </div>

                {loading ? (
                    <div className="ppw-card-body"><p className="pi-empty-text">Loading…</p></div>
                ) : (
                    <div className="ppw-card-body">
                        {error && <div className="ppw-error-msg" style={{ marginBottom: '1rem' }}>{error}</div>}

                        <div className="occ-instance-block">
                            <label className="ppw-label">Name</label>
                            <input
                                type="text"
                                className="ppw-input"
                                placeholder="e.g. Ops team"
                                value={name}
                                onChange={e => { setName(e.target.value); setNameErr(null); }}
                            />
                            {nameErr && <span className="ppw-error-msg">{nameErr}</span>}
                        </div>

                        <div className="occ-instance-block">
                            <label className="ppw-label">Description <span style={{ color: '#9ca3af' }}>(optional)</span></label>
                            <textarea
                                className="ppw-input"
                                rows={2}
                                placeholder="Who these recipients are."
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                            />
                        </div>

                        <div className="occ-instance-block">
                            <label className="ppw-label">Emails <span style={{ color: '#9ca3af' }}>(up to {MAX_EMAILS})</span></label>
                            {emails.map((em, i) => (
                                <div key={i} className="dryft-email-row">
                                    <input
                                        type="email"
                                        className="ppw-input"
                                        placeholder="name@example.com"
                                        value={em}
                                        onChange={e => setEmailAt(i, e.target.value)}
                                    />
                                    {emails.length > 1 && (
                                        <button type="button" className="occ-link-btn" onClick={() => removeEmail(i)}>Remove</button>
                                    )}
                                </div>
                            ))}
                            {emails.length < MAX_EMAILS && (
                                <button type="button" className="occ-select-btn" onClick={addEmail} style={{ marginTop: '6px' }}>
                                    + Add email
                                </button>
                            )}
                        </div>

                        <div className="occ-instance-block">
                            <label className="ppw-label">Teams channel webhook <span style={{ color: '#9ca3af' }}>(optional)</span></label>
                            <input
                                type="text"
                                className="ppw-input"
                                placeholder="https://…webhook…"
                                value={teamsWebhook}
                                onChange={e => setTeamsWebhook(e.target.value)}
                            />
                        </div>

                        <div className="occ-instance-block">
                            <label className="dryft-cfg-subarea">
                                <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
                                <span>Active</span>
                            </label>
                        </div>
                    </div>
                )}

                <div className="ppw-footer">
                    <button className="ppw-btn-back" onClick={() => navigate(BACK)} disabled={saving}>← Cancel</button>
                    <button className="ppw-btn-next" onClick={handleSave} disabled={saving || loading}>
                        {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create target')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DryftNotificationTargetForm;
